import type { AugmentingPathFrame, FindAugmentingPath } from "./types";

// Taking the closest pair first can strand two items that could both have paired
// up, which surfaces as a false removed + added. Re-chaining through an
// augmenting path recovers them without breaking an existing pair.
// The walk carries its own stack: a chain of near-identical items can run
// thousands of links deep, which recursion cannot survive.
export const findAugmentingPath: FindAugmentingPath = ({
  startBeforeIndex,
  candidatesByBeforeIndex,
  pairing,
}) => {
  const visitedAfterIndices = new Set<number>();
  const frames: AugmentingPathFrame[] = [
    {
      beforeIndex: startBeforeIndex,
      candidatePosition: 0,
      chosenCandidate: null,
    },
  ];

  while (frames.length > 0) {
    const frame = frames[frames.length - 1];
    const beforeCandidates =
      candidatesByBeforeIndex.get(frame.beforeIndex) ?? [];

    if (frame.candidatePosition >= beforeCandidates.length) {
      frames.pop();
      continue;
    }

    const candidate = beforeCandidates[frame.candidatePosition];
    frame.candidatePosition += 1;
    if (visitedAfterIndices.has(candidate.afterIndex)) continue;
    visitedAfterIndices.add(candidate.afterIndex);
    frame.chosenCandidate = candidate;

    const occupant = pairing.pairByAfterIndex.get(candidate.afterIndex);
    if (occupant !== undefined) {
      frames.push({
        beforeIndex: occupant.beforeIndex,
        candidatePosition: 0,
        chosenCandidate: null,
      });
      continue;
    }

    // Reached a free counterpart: every step of the walk shifts by one pair.
    for (const pathFrame of frames) {
      if (pathFrame.chosenCandidate === null) continue;
      pairing.pairByAfterIndex.set(
        pathFrame.chosenCandidate.afterIndex,
        pathFrame.chosenCandidate,
      );
      pairing.pairByBeforeIndex.set(
        pathFrame.beforeIndex,
        pathFrame.chosenCandidate,
      );
    }
    return true;
  }

  return false;
};
